// ===== World 12: Space bunnies – GRAVITY IN SPACE AND ROCKETS =====
// On the moon things fall slowly and fly far (level.gravity < 1). Out in space there is no gravity (gravity 0)
// and no air, so things keep moving. Planets and moons pull things in towards them (soft ring).
// The carrot rocket shoots the ball off in the direction it points. The hero is a bunny in a space helmet.
// Ball → goal: bunny in a space helmet → carrot basket (bowl) or the rocket hatch (bell).
import { defineWorld, definePart, defineObj } from '../../core/registry';
import { Physics } from '../../core/physics';
import { clamp, outline, basket } from '../../core/draw-helpers';
import type { Ctx2D, Gfx, GameBody, IntroHelpers, Placement, PhysicsApi, Rect, Reskin, CorePartType, SynthStyle, Vec, WorldDef, WorldMeta, SoundFn } from '../../core/types';
import { scene } from './scene';
import { levels } from './levels';
import { strings } from './strings';
import { K, DEG, type SpaceGoal, type RocketPl, type PlanetObj, type HatchObj } from './shared';

// ---------- constants ----------
const INK = '#3B2A5C';
const MOON_COL: [string, string] = ['#FFF3C4', '#E8C86A'];

// ---------- physics ----------
/** Pull towards a planet: centre, ring radius R, strength G and max pull amax */
interface Pull { x: number; y: number; R: number; G: number; amax: number }
/** Rocket tube: centre, direction (dx, dy), size, launch speed v, last whoosh and last firing (for the flame) */
interface Boost { x: number; y: number; dx: number; dy: number; len: number; wid: number; v: number; last: number; fireT: number | null }

// Pull towards a planet: a = G / d² (px/frame², earth's gravity = 0.278), with a cap and a soft edge at the ring
function pull(api: PhysicsApi, P: Pull) {
  const { Body } = api;
  for (const b of api.S.dyn) {
    if (b.isStatic || b.kind === 'seesaw') continue;
    const dx = P.x - b.position.x, dy = P.y - b.position.y, d = Math.hypot(dx, dy);
    if (d > P.R || d < 1) continue;
    const edge = clamp((P.R - d) / 50, 0, 1);
    const a = Math.min(P.amax, P.G / (d * d)) * edge;
    Body.setVelocity(b, { x: b.velocity.x + dx / d * a, y: b.velocity.y + dy / d * a });
  }
}
// Carrot rocket: balls in the tube are shot off in the rocket's direction (softly, so it doesn't jerk)
function boost(api: PhysicsApi, R: Boost) {
  const { Body } = api;
  for (const b of api.S.dyn) {
    if (b.isStatic || !api.isBall(b)) continue;
    const dx = b.position.x - R.x, dy = b.position.y - R.y;
    const al = dx * R.dx + dy * R.dy, pe = dx * -R.dy + dy * R.dx;
    if (Math.abs(al) > R.len / 2 + 6 || Math.abs(pe) > R.wid / 2 + 6) continue;
    // the ball is pulled onto the tube's centre line and gets the rocket's direction and speed
    const v = b.velocity, k = 0.6;
    Body.setPosition(b, { x: b.position.x + R.dy * pe * 0.3, y: b.position.y - R.dx * pe * 0.3 });
    Body.setVelocity(b, { x: v.x + (R.dx * R.v - v.x) * k, y: v.y + (R.dy * R.v - v.y) * k });
    if (api.S.t - R.last > 30) { R.last = api.S.t; R.fireT = api.S.t; api.sfx('space_whoosh', R.x, R.y, '#FFC93C', 8); }
  }
}

// ---------- drawing helpers ----------
// Local face: differs from face() in draw-helpers (bunny nose, eyes lower, pupils follow less and never shrink,
// smaller mouth, thinner lines, other cheeks; no scared look when falling).
function face(ctx: Ctx2D, r: number, v: Vec, sad: boolean | undefined) {
  const lx = clamp(v.x * 0.5, -2.5, 2.5), ly = clamp(v.y * 0.5, -2.5, 2.5);
  const sp = Math.hypot(v.x, v.y), fast = sp > 8.5;
  const ex = r * 0.34, ey = -r * 0.05, er = r * 0.25;
  for (const s of [-1, 1]) {
    ctx.beginPath(); ctx.arc(s * ex, ey, er, 0, 7); ctx.fillStyle = '#fff'; ctx.fill();
    ctx.beginPath(); ctx.arc(s * ex + lx * 0.5, ey + ly * 0.5, er * 0.55, 0, 7); ctx.fillStyle = INK; ctx.fill();
  }
  ctx.fillStyle = INK; ctx.strokeStyle = INK; ctx.lineWidth = 1.8; ctx.lineCap = 'round';
  // bunny nose
  ctx.beginPath(); ctx.ellipse(0, r * 0.2, r * 0.1, r * 0.07, 0, 0, 7); ctx.fillStyle = '#FF7FA8'; ctx.fill();
  if (sad) { ctx.beginPath(); ctx.arc(0, r * 0.6, r * 0.2, 1.2 * Math.PI, 1.8 * Math.PI); ctx.stroke(); }
  else if (fast) { ctx.beginPath(); ctx.ellipse(0, r * 0.45, r * 0.13, r * 0.17, 0, 0, 7); ctx.fillStyle = INK; ctx.fill(); }
  else { ctx.beginPath(); ctx.arc(0, r * 0.3, r * 0.2, 0.2 * Math.PI, 0.8 * Math.PI); ctx.stroke(); }
  ctx.fillStyle = 'rgba(255,120,160,.5)';
  [-1, 1].forEach(s => { ctx.beginPath(); ctx.ellipse(s * r * 0.58, r * 0.25, r * 0.13, r * 0.08, 0, 0, 7); ctx.fill(); });
}
// The bunny in a space helmet (hero) and the moon rock (helper).
// Local drawBall: differs from drawBall() in draw-helpers (helmet, ears and a crater-rock helper instead of a gradient ball).
function drawBall(ctx: Ctx2D, b: GameBody, kind: string, t: number, g: Gfx) {
  const { x, y } = b.position, r = b.circleRadius!;
  ctx.save(); ctx.translate(x, y);
  if (kind === 'helper') {
    ctx.beginPath(); ctx.arc(0, 0, r, 0, 7); ctx.fillStyle = g.lg(ctx, -r, r, '#E6E2F2', '#9E97B8'); ctx.fill(); outline(ctx, 2.5);
    ctx.save(); ctx.rotate(b.roll || 0);
    ctx.fillStyle = 'rgba(110,100,150,.45)';
    [[-r * .35, -r * .3, r * .22], [r * .4, r * .1, r * .16], [-r * .1, r * .45, r * .12]].forEach(([cx, cy, cr]) => { ctx.beginPath(); ctx.arc(cx, cy, cr, 0, 7); ctx.fill(); });
    face(ctx, r * 0.8, b.velocity, b.sad);
    ctx.restore(); ctx.restore(); return;
  }
  // suit collar
  ctx.save(); ctx.rotate(b.roll || 0);
  // the helmet glass
  ctx.beginPath(); ctx.arc(0, 0, r, 0, 7); ctx.fillStyle = 'rgba(200,240,255,.92)'; ctx.fill();
  // ears inside the helmet
  for (const s of [-1, 1]) {
    ctx.save(); ctx.translate(s * r * 0.3, -r * 0.42); ctx.rotate(s * 0.25);
    ctx.beginPath(); ctx.ellipse(0, 0, r * 0.16, r * 0.42, 0, 0, 7); ctx.fillStyle = '#fff'; ctx.fill(); ctx.lineWidth = 1.5; ctx.strokeStyle = 'rgba(59,42,92,.6)'; ctx.stroke();
    ctx.beginPath(); ctx.ellipse(0, r * 0.04, r * 0.07, r * 0.28, 0, 0, 7); ctx.fillStyle = '#FFB3CC'; ctx.fill();
    ctx.restore();
  }
  ctx.beginPath(); ctx.arc(0, r * 0.12, r * 0.58, 0, 7); ctx.fillStyle = '#fff'; ctx.fill(); ctx.lineWidth = 1.5; ctx.strokeStyle = 'rgba(59,42,92,.5)'; ctx.stroke();
  ctx.save(); ctx.translate(0, r * 0.1); face(ctx, r * 0.58, b.velocity, b.sad); ctx.restore();
  ctx.restore();
  // helmet rim + gloss (doesn't roll)
  ctx.beginPath(); ctx.arc(0, 0, r, 0, 7); ctx.lineWidth = 3.5; ctx.strokeStyle = '#FF8A3D'; ctx.stroke();
  ctx.beginPath(); ctx.arc(0, 0, r + 1.5, 0, 7); ctx.lineWidth = 1.5; ctx.strokeStyle = INK; ctx.stroke();
  ctx.beginPath(); ctx.ellipse(-r * .42, -r * .45, r * .22, r * .12, -0.7, 0, 7); ctx.fillStyle = 'rgba(255,255,255,.85)'; ctx.fill();
  ctx.restore();
}
// Carrot rocket in local coordinates: the tip points towards +x, length L
function carrot(ctx: Ctx2D, L: number, g: Gfx, t: number, fire: boolean) {
  const h = L * 0.36;
  if (fire) {   // flame out the back
    const f = 0.8 + Math.sin(t * 0.9) * 0.2;
    ctx.beginPath(); ctx.moveTo(-L / 2 + 4, -h * 0.35); ctx.quadraticCurveTo(-L / 2 - L * 0.55 * f, 0, -L / 2 + 4, h * 0.35); ctx.closePath();
    ctx.fillStyle = '#FFC93C'; ctx.fill();
    ctx.beginPath(); ctx.moveTo(-L / 2 + 4, -h * 0.18); ctx.quadraticCurveTo(-L / 2 - L * 0.3 * f, 0, -L / 2 + 4, h * 0.18); ctx.closePath();
    ctx.fillStyle = '#FF5A5F'; ctx.fill();
  }
  // green leaves at the back (like tail fins)
  ctx.fillStyle = '#37C16B';
  for (const s of [-1, 1]) { ctx.beginPath(); ctx.moveTo(-L * 0.3, s * h * 0.3); ctx.quadraticCurveTo(-L * 0.55, s * h * 1.1, -L * 0.62, s * h * 0.9); ctx.quadraticCurveTo(-L * 0.5, s * h * 0.5, -L * 0.42, s * h * 0.15); ctx.closePath(); ctx.fill(); outline(ctx, 2); }
  // carrot body
  ctx.beginPath(); ctx.moveTo(-L / 2, -h / 2); ctx.quadraticCurveTo(L * 0.2, -h * 0.62, L / 2, 0); ctx.quadraticCurveTo(L * 0.2, h * 0.62, -L / 2, h / 2); ctx.closePath();
  ctx.fillStyle = g.lg(ctx, -h / 2, h / 2, '#FFB066', '#F07A1E'); ctx.fill(); outline(ctx, 3);
  ctx.strokeStyle = 'rgba(160,70,10,.55)'; ctx.lineWidth = 2; ctx.lineCap = 'round';
  for (const [x, s] of [[-L * 0.2, -1], [0, 1], [L * 0.18, -1]]) { ctx.beginPath(); ctx.moveTo(x, s * h * 0.42); ctx.lineTo(x + L * 0.07, s * h * 0.2); ctx.stroke(); }
  // porthole
  ctx.beginPath(); ctx.arc(-L * 0.08, 0, h * 0.22, 0, 7); ctx.fillStyle = '#BFEFFF'; ctx.fill(); outline(ctx, 2.5);
  ctx.beginPath(); ctx.arc(-L * 0.1, -h * 0.07, h * 0.07, 0, 7); ctx.fillStyle = '#fff'; ctx.fill();
}
// The rocket as a part: a tube with the carrot on top and arrows showing the direction
function drawRocket(ctx: Ctx2D, x: number, y: number, a: number, g: Gfx, t: number, fireT: number | null, sel: boolean) {
  const firing = fireT != null && t - fireT < 30;
  ctx.save(); ctx.translate(x, y); ctx.rotate(a);
  // launch tube (see-through, so you can see the ball go through)
  g.rr(ctx, -46, -22, 92, 44, 18); ctx.fillStyle = 'rgba(255,190,120,.35)'; ctx.fill();
  ctx.lineWidth = 3; ctx.strokeStyle = 'rgba(240,122,30,.9)'; ctx.stroke();
  // direction arrows in the tube
  ctx.strokeStyle = 'rgba(255,255,255,.95)'; ctx.lineWidth = 4; ctx.lineCap = 'round'; ctx.lineJoin = 'round';
  for (let i = 0; i < 2; i++) { const px = -14 + i * 22 + (firing ? ((t * 3) % 10) : 0); ctx.beginPath(); ctx.moveTo(px - 8, -10); ctx.lineTo(px + 2, 0); ctx.lineTo(px - 8, 10); ctx.stroke(); }
  // the carrot sits on top of the tube
  ctx.save(); ctx.translate(0, -24); carrot(ctx, 84, g, t, firing); ctx.restore();
  if (sel) { g.rr(ctx, -52, -50, 104, 78, 18); ctx.strokeStyle = '#FFC93C'; ctx.lineWidth = 4; ctx.stroke(); }
  ctx.restore();
}
// Small moon / planet's soft pull ring (k = strength of the colours)
function drawRing(ctx: Ctx2D, x: number, y: number, R: number, t: number, k: number) {
  ctx.save();
  ctx.beginPath(); ctx.arc(x, y, R, 0, 7); ctx.fillStyle = `rgba(190,170,255,${0.1 * k})`; ctx.fill();
  ctx.setLineDash([10, 12]); ctx.lineDashOffset = -t * 0.6; ctx.lineWidth = 3; ctx.strokeStyle = `rgba(220,205,255,${0.75 * k})`; ctx.stroke(); ctx.setLineDash([]);
  // rings that shrink in towards the centre
  for (let i = 0; i < 2; i++) {
    const f = ((t * 0.008 + i / 2) % 1), rr = R * (1 - f * 0.75);
    ctx.beginPath(); ctx.arc(x, y, rr, 0, 7); ctx.lineWidth = 2.5; ctx.strokeStyle = `rgba(230,220,255,${(0.45 * Math.sin(f * Math.PI) * k).toFixed(3)})`; ctx.stroke();
  }
  ctx.restore();
}
// the homing beam's soft blue ring round the rocket hatch
function drawBeam(ctx: Ctx2D, x: number, y: number, R: number, t: number) {
  ctx.save();
  ctx.beginPath(); ctx.arc(x, y, R, 0, 7); ctx.fillStyle = 'rgba(127,211,255,.14)'; ctx.fill();
  for (let i = 0; i < 2; i++) {
    const f = ((t * 0.012 + i / 2) % 1), rr = R * (1 - f * 0.7);
    ctx.beginPath(); ctx.arc(x, y, rr, 0, 7); ctx.lineWidth = 3; ctx.strokeStyle = `rgba(160,225,255,${(0.6 * Math.sin(f * Math.PI)).toFixed(3)})`; ctx.stroke();
  }
  ctx.restore();
}
function drawPlanet(ctx: Ctx2D, x: number, y: number, r: number, col: [string, string] | undefined, g: Gfx, t: number, faceOn: boolean) {
  ctx.save(); ctx.translate(x, y);
  const c = col || ['#C9B6F2', '#8E76D4'];
  ctx.beginPath(); ctx.arc(0, 0, r, 0, 7); ctx.fillStyle = g.lg(ctx, -r, r, c[0], c[1]); ctx.fill(); outline(ctx, 3);
  ctx.fillStyle = 'rgba(80,60,140,.25)';
  [[-r * .4, -r * .3, r * .22], [r * .35, r * .35, r * .18], [r * .45, -r * .4, r * .1], [-r * .2, r * .5, r * .12]].forEach(([cx, cy, cr]) => { ctx.beginPath(); ctx.arc(cx, cy, cr, 0, 7); ctx.fill(); });
  if (faceOn) {
    for (const s of [-1, 1]) { ctx.beginPath(); ctx.arc(s * r * 0.25, -r * 0.05, r * 0.08, 0, 7); ctx.fillStyle = INK; ctx.fill(); }
    ctx.strokeStyle = INK; ctx.lineWidth = 2.5; ctx.lineCap = 'round'; ctx.beginPath(); ctx.arc(0, r * 0.1, r * 0.18, 0.2 * Math.PI, 0.8 * Math.PI); ctx.stroke();
  }
  ctx.beginPath(); ctx.ellipse(-r * .4, -r * .5, r * .2, r * .1, -0.6, 0, 7); ctx.fillStyle = 'rgba(255,255,255,.6)'; ctx.fill();
  ctx.restore();
}
// Lumpy rock with craters (asteroid and the kit's stone)
function drawRock(ctx: Ctx2D, x: number, y: number, r: number, a: number, g: Gfx, sel?: boolean) {
  ctx.save(); ctx.translate(x, y); ctx.rotate(a || 0);
  ctx.beginPath();
  for (let i = 0; i < 9; i++) { const an = i / 9 * Math.PI * 2, rr = r * (0.88 + ((i * 7) % 4) * 0.05); ctx.lineTo(Math.cos(an) * rr, Math.sin(an) * rr); }
  ctx.closePath(); ctx.fillStyle = g.lg(ctx, -r, r, '#B9AFC9', '#6F6688'); ctx.fill(); outline(ctx, 3);
  ctx.fillStyle = 'rgba(60,50,90,.3)';
  [[-r * .3, -r * .2, r * .2], [r * .3, r * .25, r * .15], [r * .2, -r * .4, r * .1]].forEach(([cx, cy, cr]) => { ctx.beginPath(); ctx.arc(cx, cy, cr, 0, 7); ctx.fill(); });
  ctx.beginPath(); ctx.ellipse(-r * .35, -r * .45, r * .2, r * .1, -0.6, 0, 7); ctx.fillStyle = 'rgba(255,255,255,.45)'; ctx.fill();
  if (sel) { ctx.beginPath(); ctx.arc(0, 0, r + 8, 0, 7); ctx.strokeStyle = '#FFC93C'; ctx.lineWidth = 4; ctx.stroke(); }
  ctx.restore();
}
// Metal girder (plank) with rivets and solar-cell panels
function girder(ctx: Ctx2D, w: number, h: number, sel: boolean, g: Gfx, c1?: string, c2?: string) {
  g.block(ctx, 0, 0, w, h, 0, c1 || '#E8EEF8', c2 || '#9AA8C2', h / 2, { outline: sel ? '#FFC93C' : null });
  ctx.fillStyle = '#4A78D8';
  for (let x = -w / 2 + 14; x < w / 2 - 22; x += 28) { g.rr(ctx, x, -h / 2 + 4, 20, h - 8, 3); ctx.fill(); }
  ctx.strokeStyle = 'rgba(255,255,255,.6)'; ctx.lineWidth = 1.5;
  for (let x = -w / 2 + 14; x < w / 2 - 22; x += 28) { ctx.beginPath(); ctx.moveTo(x + 10, -h / 2 + 4); ctx.lineTo(x + 10, h / 2 - 4); ctx.stroke(); }
}
// Energy shield: bright beam with a blue force edge
function shield(ctx: Ctx2D, w: number, h: number, sel: boolean, g: Gfx, t: number) {
  ctx.save();
  ctx.beginPath(); g.rr(ctx, -w / 2 - 6, -h / 2 - 6, w + 12, h + 12, h / 2 + 6); ctx.fillStyle = `rgba(127,211,255,${0.25 + 0.1 * Math.sin(t * 0.1)})`; ctx.fill();
  g.block(ctx, 0, 0, w, h, 0, '#E6FBFF', '#5FC3F0', h / 2, { outline: sel ? '#FFC93C' : null });
  ctx.strokeStyle = 'rgba(255,255,255,.9)'; ctx.lineWidth = 2; ctx.lineCap = 'round';
  for (let x = -w / 2 + 16; x < w / 2 - 10; x += 22) { ctx.beginPath(); ctx.moveTo(x, -h / 2 + 4); ctx.lineTo(x + 8, h / 2 - 4); ctx.stroke(); }
  ctx.restore();
}
// Satellite dish (banana): the dish is an arc below the placement point
function dish(ctx: Ctx2D, pl: Pick<Placement, 'x' | 'y' | 'a'>, sel: boolean, g: Gfx, rects: (Rect & { a: number })[]) {
  ctx.save();
  // pole and foot from the bottom of the dish
  const a = (pl.a || 0) * DEG, c = Math.cos(a), s = Math.sin(a);
  const bx = pl.x - s * 40, by = pl.y + c * 40;
  ctx.strokeStyle = INK; ctx.lineWidth = 7; ctx.lineCap = 'round'; ctx.beginPath(); ctx.moveTo(bx, by); ctx.lineTo(bx - s * 30, by + c * 30); ctx.stroke();
  ctx.strokeStyle = '#B9C4D8'; ctx.lineWidth = 4; ctx.beginPath(); ctx.moveTo(bx, by); ctx.lineTo(bx - s * 30, by + c * 30); ctx.stroke();
  // the dish
  ctx.lineCap = 'round';
  ctx.beginPath(); rects.forEach((r, i) => { const px = r.x - Math.sin(r.a) * -4, py = r.y + Math.cos(r.a) * -4; if (i) ctx.lineTo(px, py); else ctx.moveTo(px, py); });
  ctx.lineWidth = 22; ctx.strokeStyle = INK; ctx.stroke();
  ctx.lineWidth = 16; ctx.strokeStyle = sel ? '#FFE07A' : '#F2F5FA'; ctx.stroke();
  ctx.lineWidth = 4; ctx.strokeStyle = '#B9C4D8'; ctx.stroke();
  // receiver stick in the middle
  ctx.strokeStyle = INK; ctx.lineWidth = 3; ctx.beginPath(); ctx.moveTo(pl.x - s * 30, pl.y + c * 30); ctx.lineTo(pl.x + s * 10, pl.y - c * 10); ctx.stroke();
  ctx.beginPath(); ctx.arc(pl.x + s * 12, pl.y - c * 12, 6, 0, 7); ctx.fillStyle = '#FF5A5F'; ctx.fill(); outline(ctx, 2);
  ctx.restore();
}
// The carrot basket on the moon (bowl): carrots sticking up out of the kit's basket shape (draw-helpers basket)
function carrotBasket(ctx: Ctx2D, gl: GoalPos, full: number, t: number, g: Gfx) {
  const { x, y, w } = gl;
  // carrots sticking up
  [[-w * .25, -0.3], [-w * .05, 0.05], [w * .2, 0.35]].forEach(([dx, rot], i) => {
    ctx.save(); ctx.translate(x + dx, y - 62 + Math.sin(t * 0.05 + i) * 1.5); ctx.rotate(-Math.PI / 2 + rot); carrot(ctx, 54, g, t, false); ctx.restore();
  });
  basket(ctx, x, y, w, { full: full ? '#FF9F4A' : null });
}
// The rocket hatch (bell): a small rocket with a round hatch – the bunny has to get home
function hatch(ctx: Ctx2D, gl: { x: number; y: number; hitT?: number }, t: number, g: Gfx) {
  const hit = gl.hitT != null, open = hit ? clamp((t - gl.hitT!) / 20, 0, 1) : 0;
  ctx.save(); ctx.translate(gl.x, gl.y);
  // star twinkles
  ctx.strokeStyle = hit ? '#FFC93C' : 'rgba(255,255,255,.9)'; ctx.lineWidth = 3; ctx.lineCap = 'round';
  for (let i = 0; i < 4; i++) {
    const an = i * Math.PI / 2 + t * 0.02 + 0.4, d = 52 + Math.sin(t * 0.1 + i) * 3, px = Math.cos(an) * d, py = Math.sin(an) * d;
    ctx.beginPath(); ctx.moveTo(px - 5, py); ctx.lineTo(px + 5, py); ctx.moveTo(px, py - 5); ctx.lineTo(px, py + 5); ctx.stroke();
  }
  // rocket body behind the hatch
  g.shadowOn(ctx, 10, 5);
  ctx.beginPath(); ctx.moveTo(0, -78); ctx.quadraticCurveTo(40, -50, 36, 20); ctx.lineTo(-36, 20); ctx.quadraticCurveTo(-40, -50, 0, -78); ctx.closePath();
  ctx.fillStyle = g.lg(ctx, -78, 20, '#FFFFFF', '#C9D3E8'); ctx.fill(); g.shadowOff(ctx); outline(ctx, 3);
  ctx.beginPath(); ctx.moveTo(0, -78); ctx.quadraticCurveTo(22, -64, 28, -48); ctx.lineTo(-28, -48); ctx.quadraticCurveTo(-22, -64, 0, -78); ctx.closePath(); ctx.fillStyle = '#FF5A5F'; ctx.fill(); outline(ctx, 2.5);
  for (const s of [-1, 1]) { ctx.beginPath(); ctx.moveTo(s * 34, -10); ctx.lineTo(s * 54, 34); ctx.lineTo(s * 30, 22); ctx.closePath(); ctx.fillStyle = '#FF5A5F'; ctx.fill(); outline(ctx, 2.5); }
  // hatch ring
  ctx.beginPath(); ctx.arc(0, 0, 24, 0, 7); ctx.fillStyle = '#3B2A5C'; ctx.fill();
  ctx.beginPath(); ctx.arc(0, 0, 20, 0, 7); ctx.fillStyle = hit ? '#FFE07A' : '#2B2160'; ctx.fill();
  // the hatch swings open
  ctx.save(); ctx.translate(-22, 0); ctx.scale(1 - open * 0.85, 1);
  ctx.beginPath(); ctx.arc(22, 0, 21, 0, 7); ctx.fillStyle = g.lg(ctx, -21, 21, '#DDF6FF', '#8FD8FF'); ctx.fill(); outline(ctx, 2.5);
  ctx.beginPath(); ctx.arc(16, -6, 5, 0, 7); ctx.fillStyle = 'rgba(255,255,255,.85)'; ctx.fill();
  ctx.restore();
  ctx.beginPath(); ctx.arc(0, 0, 24, 0, 7); ctx.lineWidth = 4; ctx.strokeStyle = '#FFC93C'; ctx.stroke();
  ctx.restore();
}

// ---------- intro animations ----------
// dark space background behind the intro videos
function introSky(ctx: Ctx2D) {
  ctx.fillStyle = '#2E2468'; ctx.fillRect(0, 0, 460, 280);
  ctx.fillStyle = 'rgba(255,255,255,.75)';
  for (let i = 0; i < 18; i++) { ctx.beginPath(); ctx.arc((i * 97) % 460, (i * 53) % 230, 1.2 + (i % 3) * 0.6, 0, 7); ctx.fill(); }
}
// the bunny rolls into the tube and is shot off up to the right
function introRocket(ctx: Ctx2D, t: number, g: Gfx, h: IntroHelpers) {
  introSky(ctx);
  ctx.fillStyle = '#B7B2CC'; ctx.fillRect(20, 200, 200, 30);
  drawRocket(ctx, 170, 178, -0.5, g, t, t > 60 && t < 100 ? 60 : null, false);
  let bx, by;
  if (t < 60) { bx = 40 + t * 2; by = 184; }
  else { const u = t - 60; bx = 165 + u * 3.4; by = 180 - u * 1.9 + u * u * 0.004; }
  g.ball(ctx, h.fakeBall(bx, by, t < 60 ? 2 : 3, t < 60 ? 0 : -2, 16), 'hero', t);
}
function introShield(ctx: Ctx2D, t: number, g: Gfx, h: IntroHelpers) {
  introSky(ctx);
  ctx.save(); ctx.translate(250, 210); ctx.rotate(0); shield(ctx, 140, 16, false, g, t); ctx.restore();
  // the ball comes down at an angle, bounces off and flies up at an angle again
  const u = (t % 240) / 240, bx = 60 + u * 380, by = 194 - Math.abs(bx - 250) * 0.75;
  g.ball(ctx, h.fakeBall(bx, by, 3, bx < 250 ? 2 : -2, 16), 'hero', t);
}
// also used for the fixed planet (space_planet)
function introMoon(ctx: Ctx2D, t: number, g: Gfx, h: IntroHelpers) {
  introSky(ctx);
  drawRing(ctx, 300, 140, 110, t, 1); drawPlanet(ctx, 300, 140, 26, MOON_COL, g, t, true);
  // the ball flies past and bends in towards the moon
  const u = t / 240, bx = 30 + u * 380, by = 60 + Math.max(0, bx - 190) * 0.55 * Math.min(1, (bx - 190) / 120);
  g.ball(ctx, h.fakeBall(bx, Math.min(by, 240), 3, 1, 16), 'hero', t);
  ctx.strokeStyle = 'rgba(255,255,255,.4)'; ctx.setLineDash([4, 8]); ctx.lineWidth = 3; ctx.beginPath(); ctx.moveTo(30, 60); ctx.lineTo(Math.min(bx, 190), 60); ctx.stroke(); ctx.setLineDash([]);
}
function introAsteroid(ctx: Ctx2D, t: number, g: Gfx, h: IntroHelpers) {
  introSky(ctx);
  // the ball pushes the asteroid, and it keeps on flying
  const hit = 90;
  const bx = t < hit ? 40 + t * 2 : 220 + (t - hit) * 0.5;
  const ax = t < hit ? 248 : 248 + (t - hit) * 1.6;
  drawRock(ctx, Math.min(ax, 520), 140, 28, t * 0.01, g, false);
  g.ball(ctx, h.fakeBall(bx, 140, 2, 0, 16), 'hero', t);
}

// ---------- skins ----------
const skins: WorldDef['skins'] = {
  // moon ground: light lilac-grey with craters
  space_rock(ctx: Ctx2D, b: GameBody, g: Gfx) {
    const dw = b.dw!, dh = b.dh!;
    g.block(ctx, b.position.x, b.position.y, dw, dh, b.angle, '#E4DEF2', '#A69CC4', Math.min(12, dw / 2, dh / 2));
    ctx.save(); ctx.translate(b.position.x, b.position.y); ctx.rotate(b.angle);
    ctx.fillStyle = 'rgba(120,105,170,.35)';
    for (let i = 0; i < Math.floor(dw * dh / 2600) + 1; i++) {
      const cx = -dw / 2 + 14 + ((i * 53) % Math.max(1, dw - 28)), cy = -dh / 2 + 10 + ((i * 37) % Math.max(1, dh - 20));
      const cr = Math.min(3 + (i % 3) * 3, dh / 2 - 3, dw / 2 - 3);
      if (cr > 1.5) { ctx.beginPath(); ctx.ellipse(cx, cy, cr * 1.3, cr, 0, 0, 7); ctx.fill(); }
    }
    ctx.restore();
  },
  // space station: white metal with blue stripes and rivets
  space_metal(ctx: Ctx2D, b: GameBody, g: Gfx) {
    const dw = b.dw!, dh = b.dh!;
    g.block(ctx, b.position.x, b.position.y, dw, dh, b.angle, '#F4F7FC', '#A9B6CE', Math.min(9, dw / 2, dh / 2));
    ctx.save(); ctx.translate(b.position.x, b.position.y); ctx.rotate(b.angle);
    ctx.fillStyle = 'rgba(74,120,216,.7)';
    if (dw >= dh) ctx.fillRect(-dw / 2 + 6, -1.5, dw - 12, 3); else ctx.fillRect(-1.5, -dh / 2 + 6, 3, dh - 12);
    ctx.fillStyle = 'rgba(59,42,92,.45)';
    if (dw >= dh) for (let x = -dw / 2 + 12; x < dw / 2 - 6; x += 40) { ctx.beginPath(); ctx.arc(x, -dh / 2 + 5, 1.8, 0, 7); ctx.fill(); }
    else for (let y = -dh / 2 + 12; y < dh / 2 - 6; y += 40) { ctx.beginPath(); ctx.arc(-dw / 2 + 5, y, 1.8, 0, 7); ctx.fill(); }
    ctx.restore();
  },
};

// ---------- parts ----------
const parts = {
  // Carrot rocket: a ball that rolls into the tube is shot out in the direction the rocket points
  space_rocket: definePart({
    rects: [{ x: 0, y: 0, w: 92, h: 44 }], rot: 15, minA: -90, maxA: 90, defaultA: 0, sensor: true,
    draw(ctx, pl, sel, g) { drawRocket(ctx, pl.x, pl.y, (pl.a || 0) * DEG, g, performance.now() / 16, null, sel); },
    icon(ctx, g) { ctx.save(); ctx.scale(0.8, 0.8); ctx.translate(0, 10); drawRocket(ctx, 0, 0, -0.35, g, 0, null, false); ctx.restore(); },
    create(api, pl) {
      const a = (pl.a || 0) * DEG;
      const R: Boost = { x: pl.x, y: pl.y, dx: Math.cos(a), dy: Math.sin(a), len: 92, wid: 44, v: (pl as RocketPl).v || 11, last: -99, fireT: null };
      return {
        layer: 'mid',
        step(api) { boost(api, R); },
        draw(ctx, t, g, S) { drawRocket(ctx, R.x, R.y, a, g, S.t, R.fireT, false); },
      };
    },
    intro: introRocket,
  }),
  // Energy shield: shiny beam the ball bounces off (in space it keeps its speed)
  space_shield: definePart({
    rects: [{ x: 0, y: 0, w: 130, h: 16 }], rot: 15, defaultA: 0,
    create(api, pl) {
      const a = (pl.a || 0) * DEG;
      const b = api.add(api.R(pl.x, pl.y, 130, 16, { isStatic: true, angle: a, friction: 0, restitution: 0.2, chamfer: { radius: 7 }, label: 'space_shield' }));
      b.kind = 'space_shield';
      const nx = -Math.sin(a), ny = Math.cos(a), todo: { o: GameBody; v: Vec; n: Vec; vn: number }[] = [];
      let flash = -99;
      // mirror the velocity off the shield (otherwise the physics engine would let slow balls slide)
      api.onHit(b, o => {
        if (!api.isDynamic(o)) return;
        const s = (o.position.x - pl.x) * nx + (o.position.y - pl.y) * ny >= 0 ? 1 : -1;
        const vn = o.velocity.x * nx * s + o.velocity.y * ny * s;
        if (vn < -0.3) todo.push({ o, v: { x: o.velocity.x, y: o.velocity.y }, n: { x: nx * s, y: ny * s }, vn });
      });
      return {
        layer: 'mid',
        after(api) {
          for (const q of todo.splice(0)) {
            const k = -2 * q.vn * 0.96;
            api.Body.setVelocity(q.o, { x: q.v.x * 0.98 + q.n.x * k, y: q.v.y * 0.98 + q.n.y * k });
            if (api.S.t - flash > 10) { flash = api.S.t; api.sfx('space_ping', q.o.position.x, q.o.position.y, '#9FE6FF', 6); }
          }
        },
        draw(ctx, t, g) { ctx.save(); ctx.translate(pl.x, pl.y); ctx.rotate(a); shield(ctx, 130, 16, false, g, api.S.t - flash < 12 ? 20 : t); ctx.restore(); },
      };
    },
    draw(ctx, pl, sel, g) { const r = Physics.worldRects(pl)[0]; ctx.save(); ctx.translate(r.x, r.y); ctx.rotate(r.a); shield(ctx, r.w, r.h, sel, g, performance.now() / 16); ctx.restore(); },
    icon(ctx, g) { ctx.save(); ctx.rotate(-0.35); shield(ctx, 100, 16, false, g, 0); ctx.restore(); },
    intro: introShield,
  }),
  // Small moon: pulls everything inside the soft ring in towards it
  space_moon: definePart({
    rects: [{ x: 0, y: 0, w: 60, h: 60, circle: true }], circle: 30, rot: false,
    draw(ctx, pl, sel, g) {
      drawRing(ctx, pl.x, pl.y, 240, performance.now() / 16, 1);
      drawPlanet(ctx, pl.x, pl.y, 30, MOON_COL, g, 0, true);
      if (sel) { ctx.beginPath(); ctx.arc(pl.x, pl.y, 38, 0, 7); ctx.strokeStyle = '#FFC93C'; ctx.lineWidth = 4; ctx.stroke(); }
    },
    icon(ctx, g) { drawRing(ctx, 0, 0, 40, 0, 0.8); drawPlanet(ctx, 0, 0, 24, MOON_COL, g, 0, true); },
    create(api, pl) {
      const b = api.add(api.Bodies.circle(pl.x, pl.y, 30, { isStatic: true, friction: 0.6, restitution: 0.1, label: 'space_moon' }));
      b.kind = 'space_moon';
      const P: Pull = { x: pl.x, y: pl.y, R: 240, G: 3600, amax: 0.6 };
      return {
        layer: 'back',
        step(api) { pull(api, P); },
        draw(ctx, t, g) { drawRing(ctx, P.x, P.y, P.R, t, 1); drawPlanet(ctx, P.x, P.y, 30, MOON_COL, g, t, true); },
      };
    },
    intro: introMoon,
  }),
  // Asteroid: heavy rock that floats in space until something pushes it – then it keeps going
  space_asteroid: definePart({
    rects: [{ x: 0, y: 0, w: 56, h: 56, circle: true }], circle: 28, rot: false,
    draw(ctx, pl, sel, g) { drawRock(ctx, pl.x, pl.y, 28, 0, g, sel); },
    icon(ctx, g) { drawRock(ctx, 0, 0, 28, 0.4, g); },
    create(api, pl) {
      const b = api.add(api.Bodies.circle(pl.x, pl.y, 28, { density: 0.006, friction: 0.2, restitution: 0.4, frictionAir: 0, label: 'space_asteroid' }));
      b.kind = 'space_asteroid'; api.dynamic(b);
      api.onHit(b, o => { if (api.isBall(o) || o.kind === 'space_asteroid') api.sfx('space_thud', b.position.x, b.position.y, '#C9BFE0', 6); });
      return { layer: 'mid', draw(ctx, t, g) { drawRock(ctx, b.position.x, b.position.y, 28, b.angle, g, false); } };
    },
    intro: introAsteroid,
  }),
};

// ---------- objects ----------
interface HatchState { b: GameBody; open: number; opening: boolean }
const objs = {
  // Fixed planet in the level: { x, y, r, R (ring), G, col }
  space_planet: defineObj<{ P: Pull }>({
    layer: 'back',
    create(api, od) {
      const o = od as PlanetObj;
      const b = api.add(api.Bodies.circle(o.x, o.y, o.r || 50, { isStatic: true, friction: 0.6, restitution: 0.1, label: 'space_planet' }));
      b.kind = 'space_planet';
      return { P: { x: o.x, y: o.y, R: o.R || 240, G: o.G || 4000, amax: o.amax || 0.6 } };
    },
    step(api, st) { pull(api, st.P); },
    blockers(od) { const o = od as PlanetObj; return [{ x: o.x, y: o.y, r: (o.r || 50) + 10 }]; },
    draw(ctx, st, t, g) { const o = st.o as PlanetObj; drawRing(ctx, o.x, o.y, st.P.R, t, 1); drawPlanet(ctx, o.x, o.y, o.r || 50, o.col, g, t, true); },
    intro: introMoon,
  }),
  // Hatch that slides away when a button is pressed: { x, y, w, h, id }
  space_hatch: defineObj<HatchState>({
    layer: 'mid',
    create(api, od) {
      const o = od as HatchObj;
      const b = api.add(api.R(o.x, o.y, o.w || 20, o.h || 140, { isStatic: true, friction: 0.1, restitution: 0.1, chamfer: { radius: 6 } }));
      b.kind = 'space_hatch';
      const st: HatchState = { b, open: 0, opening: false };
      api.onAct(o.id, () => { st.opening = true; api.sfx('space_beep', o.x, o.y); });
      return st;
    },
    step(api, st) {
      if (!st.opening || st.open >= 1) return;
      st.open = Math.min(1, st.open + 0.04);
      const o = st.o as HatchObj, h = o.h || 140;
      api.Body.setPosition(st.b, { x: o.x, y: o.y - st.open * (h + 10) });
    },
    busy(api, st) { return st.opening && st.open < 1; },
    draw(ctx, st, t, g) {
      const b = st.b, o = st.o as HatchObj;
      g.block(ctx, b.position.x, b.position.y, o.w || 20, o.h || 140, 0, '#FFE07A', '#E0A316', 6);
      ctx.save(); ctx.translate(b.position.x, b.position.y);
      ctx.strokeStyle = INK; ctx.lineWidth = 3;
      for (let y = -(o.h || 140) / 2 + 14; y < (o.h || 140) / 2 - 8; y += 22) { ctx.beginPath(); ctx.moveTo(-(o.w || 20) / 2 + 3, y); ctx.lineTo((o.w || 20) / 2 - 3, y + 10); ctx.stroke(); }
      ctx.restore();
    },
  }),
};

// ---------- sounds ----------
const sounds: Record<string, SoundFn> = {
  space_whoosh: (tone, noise) => { noise(0.45, 0.3, 700, 0.6); tone(180, 0.4, 'sawtooth', 0.06, 520); },
  space_beep: (tone) => { tone(1320, 0.08, 'sine', 0.12); tone(1760, 0.08, 'sine', 0.1, null, 0.09); },
  space_ping: (tone) => { tone(880, 0.15, 'triangle', 0.14, 1320); },
  space_thud: (tone, noise) => { noise(0.2, 0.25, 400, 1.2); tone(110, 0.25, 'sine', 0.18, 60); },
};

// ---------- reskins (kit parts, balls and goals in space style) ----------
const reskin: Partial<Record<CorePartType, Reskin>> = {
  plank(ctx, pl, sel, g, rects) { const r = rects[0]; ctx.save(); ctx.translate(r.x, r.y); ctx.rotate(r.a); girder(ctx, r.w, r.h, sel, g); ctx.restore(); },
  long_plank(ctx, pl, sel, g, rects) { const r = rects[0]; ctx.save(); ctx.translate(r.x, r.y); ctx.rotate(r.a); girder(ctx, r.w, r.h, sel, g, '#FFF3C4', '#E0B04A'); ctx.restore(); },
  pad(ctx, pl, sel, g, rects) {
    // moon trampoline: soft pink membrane on a ring
    const r = rects[0];
    ctx.save(); ctx.translate(r.x, r.y); ctx.rotate(r.a);
    for (const s of [-1, 1]) { ctx.beginPath(); ctx.moveTo(s * (r.w / 2 - 12), r.h / 2); ctx.lineTo(s * (r.w / 2 - 4), r.h / 2 + 14); ctx.lineWidth = 6; ctx.strokeStyle = INK; ctx.lineCap = 'round'; ctx.stroke(); }
    g.block(ctx, 0, 0, r.w, r.h, 0, '#FFB3D6', '#E0569E', r.h / 2, { outline: sel ? '#FFC93C' : null });
    ctx.strokeStyle = 'rgba(255,255,255,.75)'; ctx.lineWidth = 2;
    for (let x = -r.w / 2 + 14; x < r.w / 2 - 8; x += 14) { ctx.beginPath(); ctx.moveTo(x, -r.h / 2 + 3); ctx.lineTo(x + 6, r.h / 2 - 3); ctx.stroke(); }
    ctx.restore();
  },
  banana: dish,
  stone(ctx, pl, sel, g) { const body = pl.body as GameBody | undefined; drawRock(ctx, pl.x, pl.y, 24, body ? body.angle : 0, g, sel); },
  seesaw(ctx, pl, sel, g) {
    // metal girder on a moon rock
    const x = pl.x, y = pl.y;
    ctx.save(); g.shadowOn(ctx, 8, 4);
    ctx.beginPath(); ctx.moveTo(x, y + 2); ctx.lineTo(x - 26, y + 42); ctx.lineTo(x + 26, y + 42); ctx.closePath();
    ctx.fillStyle = g.lg(ctx, y, y + 42, '#E4DEF2', '#A69CC4'); ctx.fill(); g.shadowOff(ctx); outline(ctx, 3); ctx.restore();
    ctx.save(); ctx.translate(x, y); ctx.rotate((pl.a || 0) * DEG); girder(ctx, 230, 14, sel, g, '#FFF3C4', '#E0B04A'); ctx.restore();
    ctx.beginPath(); ctx.arc(x, y, 7, 0, 7); ctx.fillStyle = '#fff'; ctx.fill(); outline(ctx, 3);
  },
};
const icons: WorldDef['icons'] = {
  plank(ctx, g) { ctx.save(); ctx.rotate(-0.35); girder(ctx, 100, 18, false, g); ctx.restore(); },
  long_plank(ctx, g) { ctx.save(); ctx.rotate(-0.35); girder(ctx, 112, 16, false, g, '#FFF3C4', '#E0B04A'); ctx.restore(); },
  pad(ctx, g) { reskin.pad!(ctx, { t: 'pad', x: 0, y: 0, a: 0 }, false, g, [{ x: 0, y: -6, a: 0, w: 86, h: 16 }]); },
  banana(ctx, g) { ctx.save(); ctx.scale(0.55, 0.55); dish(ctx, { x: 0, y: -20, a: 0 }, false, g, Physics.worldRects({ t: 'banana', x: 0, y: -20, a: 0 })); ctx.restore(); },
  stone(ctx, g) { drawRock(ctx, 0, 0, 26, 0.3, g); },
};
type GoalPos = { x: number; y: number; w: number };
const goal = {
  bowl(ctx: Ctx2D, gl: GoalPos, full: number, t: number, g: Gfx) { carrotBasket(ctx, gl, full, t, g); },
  front(ctx: Ctx2D, gl: GoalPos) { const { x, y, w } = gl; ctx.save(); ctx.beginPath(); ctx.ellipse(x, y - 58, w / 2 + 6, 9, 0, 0, Math.PI); ctx.lineWidth = 5; ctx.strokeStyle = '#F5D08A'; ctx.stroke(); ctx.restore(); },
  bell(ctx: Ctx2D, gl: { x: number; y: number; hitT?: number }, t: number, g: Gfx) { hatch(ctx, gl, t, g); },
};

// ---------- meta ----------
const synth: SynthStyle = { bpm: 60, root: 185.0, sc: 'lydian', prog: [0, 1, 0, 4], bar: 4, lead: 'soft', dens: 0.25 };
const meta: WorldMeta = {
  icon: '🐰', strings, music: 'space.mp3', synth,
  credits: { title: 'Space Music', artist: 'PaulYudin', id: 607605, url: 'https://pixabay.com/music/ambient-space-space-music-607605/' },
};

export default defineWorld({
  k: K,
  meta,
  scene,
  wash: 'rgba(40,30,100,.55)',
  setup(api, level) {
    // no air in space: balls and rocks don't lose speed (level.air overrides)
    for (const b of api.S.dyn) if (b.kind === 'hero' || b.kind === 'helper' || b.kind === 'stone') b.frictionAir = level.air != null ? level.air as number : 0;
    // homing beam: the rocket hatch pulls the bunny in the last bit (goal.beam = range)
    const gl = level.goal as SpaceGoal;
    if (gl.type === 'bell' && gl.beam) {
      const beam = gl.beam;
      api.thing({
        layer: 'back',
        step(api) {
          for (const b of api.S.heroes) {
            if (b.isStatic) continue;
            const dx = gl.x - b.position.x, dy = gl.y - b.position.y, d = Math.hypot(dx, dy);
            if (d > beam || d < 1) continue;
            const a = 0.35 * clamp((beam - d) / 30, 0, 1);
            api.Body.setVelocity(b, { x: b.velocity.x * 0.97 + dx / d * a, y: b.velocity.y * 0.97 + dy / d * a });
          }
        },
        draw(ctx, t) { drawBeam(ctx, gl.x, gl.y, beam, t); },
      });
    }
  },
  bgExtra(ctx, W, H, level) {
    // a faint extra starry sky, so space feels deep
    ctx.fillStyle = 'rgba(255,255,255,.7)';
    for (let i = 0; i < 40; i++) { const x = (i * 157) % W, y = (i * 89) % H; ctx.beginPath(); ctx.arc(x, y, 1 + (i % 3) * 0.6, 0, 7); ctx.fill(); }
    if (level.gravity === 0) {   // deep space: no moon ground, just stars
      ctx.fillStyle = 'rgba(20,14,60,.3)'; ctx.fillRect(0, 0, W, H);
    }
  },
  skins,
  ball: drawBall,
  goal,
  sounds,
  reskin,
  icons,
  parts,
  objs,
  levels,
});
